import 'System/Promises'
import 'Data/TransactionItem'
import '../Form'
import './Controls'
import { ValidationError } from '../../../errors'

/** @namespace UI.Widget.Forms */
NS("UI.Widget.Forms");

const MODE_VIEW = "view";
const MODE_EDIT = "edit";

/**
 * @class
 * @extends UI.Widget.Form
 * @extends Data.TransactionItem
 * @extends UI.Widget.Forms.Controls
 * @extends System.Promises
 */
UI.Widget.Forms.ViewEditForm = Class(UI.Widget.Form, Data.TransactionItem, UI.Widget.Forms.Controls,
    System.Promises,
    /** @lends UI.Widget.Forms.ViewEditForm.prototype */
    {
        // options
        /** @type {string} */
        securityKey: null,
        /** @type {Object} */
        item: null,
        saveMethod: "save",
        /** @type {Object} */
        saveOptions: {},
        /** @type {string} */
        sourceClass: null,
        source: null,
        mode: MODE_VIEW,
        onBeforeHideControl: _.noop,
        /**
         * @inheritDoc
         */
        initialize()
        {
            this.ParentCall();

            this.source = this.source || Util.create(this.sourceClass);
            this.styles.push("view-edit-form");

            this.initItem();
            this.commitItem();
        },
        /**
         * @protected
         */
        initItem()
        {
            this.item || (this.item = this.source.create());
        },
        /** @inheritDoc */
        render(options = {}) {
            this.Parent({
                ...options,
                silent: true
            });

            _.defer(() => !options.silent && this.emit('ready'));

            return this;
        },
        /**
         * @returns {UI.Widget.Forms.ViewEditForm}
         */
        destroy()
        {
            this.unbindControl();
            this.rollbackItem();
            this.cancelAllPromises();

            this.item = null;
            this.source = null;

            return this.ParentCall();
        },
        /**
         * Saves current item state to the server.
         *
         * @fires UI.Widget.Forms.ViewEditForm#before.save
         * @fires UI.Widget.Forms.ViewEditForm#save
         * @param {boolean} [silent] Save without emitting event
         * @returns {Promise.<Object>}
         */
        async save (silent) {
            const saved = (item) => {
                this.commitItem();
                this.updateControl();

                if (!silent) {
                    this.emit("save", { item });
                }

                return item;
            };

            if (!this.canSave()) {
                throw new ValidationError('Недостаточно прав для выполнения операции сохранения')
            }
            if (!this.validate()) {
                throw new ValidationError("Невозможно сохранить неверные данные.\nПроверьте правильность заполнения формы.")
            }

            this.emit("before.save", {
                item: this.item
            });

            if (!this.saveMethod) {
                return saved(this.item);
            }

            const data = await this.addPromise("itemSave", this.source[ this.saveMethod ](this.item))
            Object.assign(this.item, data.item)

            return saved(this.item)
        },
        /**
         * Rollbacks item state
         * @fires UI.Widget.Forms.ViewEditForm#cancel
         * @param {boolean} [silent] Cancel without emitting event
         * @returns {boolean}
         */
        cancel(silent) {
            this.mode = this.Class.Mode.View;

            this.cancelPromise("itemSave");
            this.rollbackItem();
            this.hideControls(this.element);

            if (!silent) {
                this.emit("cancel", {
                    item: this.item
                });
            }

            return true;
        },
        /**
         * @fires UI.Widget.Forms.ViewEditForm#edit
         * @param {boolean} [silent] Executes without events
         * @returns {boolean}
         */
        edit(silent) {
            if (!this.canSave()) {
                alert("Доступ запрещен");
                return false;
            }

            if (this.isEditMode()) {
                return false;
            }

            this.mode = this.Class.Mode.Edit;
            this.commitItem();
            this.showControls(this.element);

            if (!silent) {
                this.emit("edit", { item: this.item });
            }

            return true;
        },
        /**
         * @returns {boolean}
         */
        canSave ()
        {
        	if (this.item.id)
            	return check(this.securityKey, [Security.AccessMode.Update]);
        	else
				return check(this.securityKey, [Security.AccessMode.Insert]);
        },
        /**
         * @returns {boolean}
         */
        canRemove ()
        {
            return check(this.securityKey, Security.AccessMode.Remove);
        },
        /**
         * @returns {boolean}
         */
        isEditMode()
        {
            return this.mode === this.Class.Mode.Edit;
        },
        /**
         * @param {Element} elem
         * @returns {Object} Returns hash of control options
         */
        getControlOptions (elem) {
            return _.reduce($(elem).data(), function(options, value, key) {
                if (/^control.*$/i.test(key)) {
                    if (_.isObject(value)) {
                        value = JSON.stringify(value);
                    }

                    options[_.kebabCase(key.substr(7))] = value;
                }

                return options;
            }, {});
        },
        /**
         * @param {Element} container
         * @returns {Promise}
         */
        addControls (container) {
            $(container).find("[data-control-type]").each((i, elem) => {
                const $elem = $(elem);
                var options = this.getControlOptions(elem);
                options.mode = this.mode;
                const $control = $("<control />").attr(options).insertAfter(elem);

                this.bindControl($control);
                const control = this.findControl($control);

                control.update();

                if (this.isEditMode() || control.smartControl) {
                    $elem.hide();
                } else {
                    control.$element.filter(':not(style)').hide();
                }
            });

            return this;
        },
        /**
         * @param {Element} container
         * @returns {UI.Widget.Forms.ViewEditForm}
         */
        showControls (container) {
            $(container).find("[data-control-type]").each((i, elem) => {
                const $elem = $(elem);

                $elem.hide();
                const control = this.findControl($elem.next());

                if (control) {
                    if (control.smartControl) {
                        control.update();
                        control.setMode('edit');
                    } else {
                        control.update();
                        control.$element.filter(':not(style)').show();
                    }
                }
            });

            return this;
        },
        /**
         * @param {Element} container
         * @returns {UI.Widget.Forms.ViewEditForm}
         */
        hideControls(container) {
            $(container).find("[data-control-type]").each((i, elem) => {
                const $elem = $(elem);
                const control = this.findControl($elem.next());
                var value;

                if (!control) {
                    return;
                }

                if (control.smartControl) {
                    control.setMode('view');
                } else {
                    control.$element.filter(':not(style)').hide();

                    if (control.Is(UI.Widget.Forms.Control.Colorpicker)) {
                        value = control.getFormattedValue("rgbstring");
                    } else {
                        value = control.getValue();
                    }

                    if (!value) {
                        value = Util.coalesce($elem.data("control-html-value"), "");
                    }

                    if (
                        control.Is(UI.Widget.Forms.Control.EnumSelect) ||
                        control.Is(UI.Widget.Forms.Control.EnumCheckbox) ||
                        control.Is(UI.Widget.Forms.Control.EnumRadio)
                    ) {
                        try {
                            eval("value = " + control.$control.attr("enum") + ".title(" + value + ");");
                        } catch (e) {}
                    }

                    if (
                        control.Is(UI.Widget.Forms.Control.Checkbox)
                    ) {
                        try {
                            value = !!value ? 'Да' : 'Нет'
                        } catch (e) {}
                    }

                    if (Class.Is(control, UI.Widget.Forms.Control.Point)) {
                        value = value && value.coordinates ? value.coordinates.join(' ') : ''
                    }

                    var args = {
                        control: control,
                        $control: $elem.next(),
                        value: value
                    };
                    Util.call(self.onBeforeHideControl, this, [args]);

                    if ($elem.data("control-update-view") == null || $elem.data("control-update-view")) {
                        if (control.Is(UI.Widget.Forms.Control.Colorpicker)) {
                            $elem.find("[name='color']").css("background", Util.coalesce(value, "none"));
                            $elem.find("[name='value']").text(Util.coalesce(value, ""));
                        } else {
                            $elem.text(Util.coalesce(value, ""));
                        }
                    }

                    $elem.show();
                }
            });

            return this;
        },
        /**
         * Validate fields
         *
         * @return {boolean}
         */
        validate() {
            this.clearErrors();

            return true;
        },
        /**
         * Mark control with error class.
         *
         * @param {Element} elem
         */
        setError(elem) {
            $(elem).addClass('error').one('focus', function() {
                this.classList.remove('error');
            });
        },
        /**
         * Clears all error controls.
         */
        clearErrors() {
            this.$body.find('.error').each(function() {
                this.classList.remove('error');
            });
        },
        /**
         * @protected
         */
        onEditClick(el, evt)
        {
            evt.stopPropagation();
            this.edit();
        },
        /**
         * @protected
         */
        onSaveClick (el, evt)
        {
            evt.stopPropagation();

            return this.save()
                .catch(err => alert(err.message));
        },
        /**
         * @protected
         */
        onCancelClick(el, evt)
        {
            evt.stopPropagation();
            this.cancel();
        }
    });

UI.Widget.Forms.ViewEditForm.Static( /** @lends UI.Widget.Forms.ViewEditForm */ {
    Mode: {
        View: MODE_VIEW,
        Edit: MODE_EDIT
    }
});
